import {
	AMM_RESERVE_PRECISION_EXP,
	BigNum,
	LiquidationType,
	PositionDirection,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	ENUM_UTILS,
	calculateAverageEntryPrice,
} from '@velocity-exchange/common';
import {
	AggregateLiqState,
	GroupedLiqState,
	LiqdBankBalanceInfo,
	LiqdPerpInfo,
	LiqdPerpPnlInfo,
} from 'src/hooks/Liquidations/useAggregateLiquidationDataForUser';

/**
 * Used by user liquidation table and general liquidation table to summarize groups of liquidations into a readable summary
 *
 * @param groupedLiqLookup - Map of liquidation records grouped by user
 */
export const getAggregateLiqs = (
	groupedLiqLookup: GroupedLiqState
): AggregateLiqState[] => {
	let newAggregateLiqState: AggregateLiqState[] = [];

	Object.entries(groupedLiqLookup).forEach(([liqId, groupedLiqs]) => {
		const id = liqId;
		const ts = Math.max(...groupedLiqs.map((liq) => liq.ts.toNumber()));
		const liquidations = groupedLiqs;

		let notionalAmount = BigNum.zero(QUOTE_PRECISION_EXP);

		const liqdPerpsLookup: { [symbol: string]: LiqdPerpInfo } = {};
		const liqdDepositsLookup: { [symbol: string]: LiqdBankBalanceInfo } = {};
		const liqdBorrowsLookup: { [symbol: string]: LiqdBankBalanceInfo } = {};
		const liqdPerpPnlLookup: { [symbol: string]: LiqdPerpPnlInfo } = {};

		liquidations.forEach((liq) => {
			if (
				ENUM_UTILS.match(liq.liquidationType, LiquidationType.LIQUIDATE_SPOT)
			) {
				const record = liq.liquidateSpot;
				const liabilityAssetSymbol =
					UIMarket.spotMarkets[record.liabilityMarketIndex].symbol;

				const borrowBaseSize = record.liabilityTransfer;
				const borrowQuoteSize = borrowBaseSize
					.mul(record.liabilityPrice)
					.shiftTo(QUOTE_PRECISION_EXP);

				const previousBorrowAggregate = liqdBorrowsLookup[liabilityAssetSymbol];

				const newBorrowQuoteSize = borrowQuoteSize.add(
					previousBorrowAggregate?.quoteSize ??
						BigNum.zero(borrowQuoteSize.precision)
				);

				const newBorrowBaseSize = borrowBaseSize.add(
					previousBorrowAggregate?.baseSize ??
						BigNum.zero(borrowBaseSize.precision)
				);

				liqdBorrowsLookup[liabilityAssetSymbol] = {
					bankIndex: record.liabilityMarketIndex,
					quoteSize: newBorrowQuoteSize,
					baseSize: newBorrowBaseSize,
					avgPrice: calculateAverageEntryPrice(
						newBorrowQuoteSize,
						newBorrowBaseSize
					),
				};

				const depositAssetSymbol =
					UIMarket.spotMarkets[record.assetMarketIndex].symbol;

				const depositBaseSize = record.assetTransfer;
				const depositQuoteSize = depositBaseSize
					.mul(record.assetPrice)
					.shiftTo(QUOTE_PRECISION_EXP);

				const previousDepositAggregate = liqdDepositsLookup[depositAssetSymbol];

				const newDepositQuoteSize = depositQuoteSize.add(
					previousDepositAggregate?.quoteSize ??
						BigNum.zero(depositQuoteSize.precision)
				);

				const newDepositBaseSize = depositBaseSize.add(
					previousDepositAggregate?.baseSize ??
						BigNum.zero(depositBaseSize.precision)
				);

				liqdDepositsLookup[depositAssetSymbol] = {
					bankIndex: record.assetMarketIndex,
					quoteSize: newDepositQuoteSize,
					baseSize: newDepositBaseSize,
					avgPrice: calculateAverageEntryPrice(
						newDepositQuoteSize,
						newDepositBaseSize
					),
				};

				notionalAmount = notionalAmount.add(depositQuoteSize);
				notionalAmount = notionalAmount.add(borrowQuoteSize);
			} else if (
				ENUM_UTILS.match(
					liq.liquidationType,
					LiquidationType.LIQUIDATE_BORROW_FOR_PERP_PNL
				)
			) {
				const record = liq.liquidateBorrowForPerpPnl;

				const liabilityAssetSymbol =
					UIMarket.spotMarkets[record.liabilityMarketIndex].symbol;

				const borrowBaseSize = record.liabilityTransfer;
				const borrowQuoteSize = borrowBaseSize
					.mul(record.liabilityPrice)
					.shiftTo(QUOTE_PRECISION_EXP);

				const previousBorrowAggregate = liqdBorrowsLookup[liabilityAssetSymbol];

				const newBorrowQuoteSize = borrowQuoteSize.add(
					previousBorrowAggregate?.quoteSize ??
						BigNum.zero(borrowQuoteSize.precision)
				);

				const newBorrowBaseSize = borrowBaseSize.add(
					previousBorrowAggregate?.baseSize ??
						BigNum.zero(borrowBaseSize.precision)
				);

				liqdBorrowsLookup[liabilityAssetSymbol] = {
					bankIndex: record.liabilityMarketIndex,
					quoteSize: newBorrowQuoteSize,
					baseSize: newBorrowBaseSize,
					avgPrice: calculateAverageEntryPrice(
						newBorrowQuoteSize,
						newBorrowBaseSize
					),
				};

				notionalAmount = notionalAmount.add(borrowQuoteSize);

				const perpMarket = UIMarket.perpMarkets[record.perpMarketIndex];
				const perpMarketSymbol = perpMarket.symbol;

				const direction = record.pnlTransfer.gteZero()
					? PositionDirection.LONG
					: PositionDirection.SHORT;

				const pnlSize = record.pnlTransfer;
				const pnlTransferQuoteSize = record.pnlTransfer
					.abs()
					.shiftTo(QUOTE_PRECISION_EXP);

				const previousAggregate = liqdPerpPnlLookup[perpMarketSymbol];

				const newPnlSize = pnlSize.add(
					previousAggregate?.pnlSize ?? BigNum.zero(pnlSize.precision)
				);

				const newTransferQuoteSize = pnlTransferQuoteSize.add(
					previousAggregate?.transferQuoteSize ??
						BigNum.zero(pnlTransferQuoteSize.precision)
				);

				liqdPerpPnlLookup[perpMarketSymbol] = {
					market: perpMarket,
					direction,
					pnlSize: newPnlSize,
					transferQuoteSize: newTransferQuoteSize,
				};

				notionalAmount = notionalAmount.add(pnlTransferQuoteSize);
			} else if (
				ENUM_UTILS.match(liq.liquidationType, LiquidationType.LIQUIDATE_PERP)
			) {
				const record = liq.liquidatePerp;
				const market = UIMarket.perpMarkets[record.marketIndex];

				const direction: PositionDirection = record.baseAssetAmount.gteZero()
					? PositionDirection.LONG
					: PositionDirection.LONG;
				const baseSize: BigNum = record.baseAssetAmount;
				const quoteSize: BigNum = record.quoteAssetAmount;
				const lpSharesBurned: BigNum =
					record.lpShares ?? BigNum.zero(AMM_RESERVE_PRECISION_EXP);

				const previousAggregate = liqdPerpsLookup[market.baseAssetSymbol];

				const newAggregateBaseSize = baseSize.add(
					previousAggregate?.baseSize ?? BigNum.zero(baseSize.precision)
				);
				const newAggregateQuoteSize = quoteSize.add(
					previousAggregate?.quoteSize ?? BigNum.zero(quoteSize.precision)
				);
				const currentRecordFee =
					record.liquidatorFee?.add(record.ifFee ?? BigNum.zero()) ??
					record.ifFee ??
					BigNum.zero();
				const newAggregateFee = currentRecordFee.add(
					previousAggregate?.fee ?? BigNum.zero(quoteSize.precision)
				);
				const newLpSharesBurned = lpSharesBurned.add(
					previousAggregate?.lpShares ?? BigNum.zero(AMM_RESERVE_PRECISION_EXP)
				);

				liqdPerpsLookup[market.baseAssetSymbol] = {
					market,
					direction,
					baseSize: newAggregateBaseSize,
					quoteSize: newAggregateQuoteSize,
					avgPrice: calculateAverageEntryPrice(
						newAggregateQuoteSize,
						newAggregateBaseSize
					),
					fee: newAggregateFee,
					lpShares: newLpSharesBurned,
				};

				notionalAmount = notionalAmount.add(quoteSize);
			} else if (
				ENUM_UTILS.match(
					liq.liquidationType,
					LiquidationType.LIQUIDATE_PERP_PNL_FOR_DEPOSIT
				)
			) {
				const record = liq.liquidatePerpPnlForDeposit;

				const perpMarket = UIMarket.perpMarkets[record.perpMarketIndex];

				const perpMarketSymbol = perpMarket.symbol;

				const direction = record.assetTransfer.gteZero()
					? PositionDirection.LONG
					: PositionDirection.SHORT;

				const pnlSize = record.pnlTransfer;
				const pnlTransferQuoteSize = record.pnlTransfer
					.abs()
					.shiftTo(QUOTE_PRECISION_EXP);

				const previousAggregate = liqdPerpPnlLookup[perpMarketSymbol];

				const newPnlSize = pnlSize.add(
					previousAggregate?.pnlSize ?? BigNum.zero(pnlSize.precision)
				);

				const newTransferQuoteSize = pnlTransferQuoteSize.add(
					previousAggregate?.transferQuoteSize ??
						BigNum.zero(pnlTransferQuoteSize.precision)
				);

				liqdPerpPnlLookup[perpMarketSymbol] = {
					market: perpMarket,
					direction,
					pnlSize: newPnlSize.neg(),
					transferQuoteSize: newTransferQuoteSize,
				};

				notionalAmount = notionalAmount.add(pnlTransferQuoteSize);

				const depositMarket = UIMarket.spotMarkets[record.assetMarketIndex];
				const depositMarketSymbol = depositMarket.symbol;

				const depositBaseSize = record.assetTransfer;
				const depositQuoteSize = depositBaseSize
					.mul(record.assetPrice)
					.shiftTo(QUOTE_PRECISION_EXP);

				const previousDepositAggregate =
					liqdDepositsLookup[depositMarketSymbol];

				const newDepositQuoteSize = depositQuoteSize.add(
					previousDepositAggregate?.quoteSize ??
						BigNum.zero(depositQuoteSize.precision)
				);

				const newDepositBaseSize = depositBaseSize.add(
					previousDepositAggregate?.baseSize ??
						BigNum.zero(depositBaseSize.precision)
				);

				liqdDepositsLookup[depositMarketSymbol] = {
					bankIndex: record.assetMarketIndex,
					quoteSize: newDepositQuoteSize,
					baseSize: newDepositBaseSize,
					avgPrice: calculateAverageEntryPrice(
						newDepositQuoteSize,
						newDepositBaseSize
					),
				};

				notionalAmount = notionalAmount.add(depositQuoteSize);
			}
		});

		const liqdPerps = Object.values(liqdPerpsLookup);
		const liqdDeposits = Object.values(liqdDepositsLookup);
		const liqdBorrows = Object.values(liqdBorrowsLookup);

		const liqdPerpPnls = Object.values(liqdPerpPnlLookup);

		const positionsAffected = liqdPerps.length;
		const borrowsAffected = liqdBorrows.length + liqdDeposits.length;
		const assetsAffected = liqdPerpPnls.length;

		newAggregateLiqState.push({
			ts,
			id,
			notionalAmount,
			summary: {
				positionsAffected,
				borrowsAffected,
				assetsAffected,
			},
			liqdPerps,
			liqdBorrows,
			liqdDeposits,
			liqdPerpPnls,
			liquidations,
		});
	});

	newAggregateLiqState = newAggregateLiqState.sort((liqA, liqB) => {
		return (
			Math.max(...liqB.liquidations.map((liq) => liq.slot)) -
			Math.max(...liqA.liquidations.map((liq) => liq.slot))
		);
	});

	return newAggregateLiqState;
};

export const getDescriptionForLiqRecord = (liqRecord: AggregateLiqState) => {
	const positionsCount = liqRecord.summary.positionsAffected;
	const balancesCount = liqRecord.summary.borrowsAffected;

	const positionsString = positionsCount
		? `${positionsCount} position${positionsCount > 1 ? 's' : ''}`
		: '';

	const balancesString = balancesCount
		? `${balancesCount} balance${balancesCount > 1 ? 's' : ''}`
		: '';

	const strings = [positionsString, balancesString].filter(
		(string) => string !== ''
	);

	const coreString = strings
		.map((str, index) => {
			return index === 0
				? str
				: index === strings.length - 1
					? `, and ${str}`
					: `, ${str}, `;
		})
		.join('');

	return `Impacted: ${coreString}`;
};
